import React from "react";
import styles from "../../../styles/AdminConsole.module.css";
import { formatDate } from "../../../utils/utils";
import Toggle from "../shared/components/Toggle";
import { MILESTONES } from "../../../data/constants";

/**
 * VisitSubTable
 *
 * The expandable sub-table rendered beneath each order row.
 * Wrapped in React.memo — only re-renders when its own visits array
 * or deleteConfirm key changes, not when other orders update.
 *
 * Props:
 *   orderId        string
 *   visits         Visit[]
 *   isOpen         bool
 *   canWrite       bool
 *   isAdmin        bool
 *   masterColCount number   — colSpan for the wrapper cell
 *   deleteConfirm  string | null  — "visit:<id>" when confirm is pending
 *
 *   onOpenAddVisit    (order) => void   — pass the full order object
 *   order             object            — needed to call onOpenAddVisit
 *   onOpenEditVisit   (orderId, visit) => void
 *   onDeleteVisit     (orderId, visit) => void
 *   onToggleCleared   (orderId, visit) => void
 *   onToggleFlagged   (orderId, visit) => void
 *   onCancelDelete    () => void
 *   onCancelDeleteRequest  (orderId, visit) => void  — QC member cancels their own pending delete request
 */
const VisitSubTable = React.memo(function VisitSubTable({
  orderId,
  visits,
  isOpen,
  canWrite,
  isAdmin,
  masterColCount,
  deleteConfirm,
  order,
  onOpenAddVisit,
  onOpenEditVisit,
  onDeleteVisit,
  onToggleCleared,
  onToggleFlagged,
  onCancelDelete,
  onCancelDeleteRequest,
}) {
  return (
    <tr className={styles.expandedRow}>
      <td className={styles.expandedCell} colSpan={masterColCount}>
        <div
          className={`${styles.subTableCollapse} ${isOpen ? styles.subTableCollapseOpen : ""}`}
        >
          <div className={styles.subTableCollapseInner}>
            {visits.length === 0 ? (
              <div className={styles.subTableEmpty}>
                No visits logged yet for this order.
                {canWrite && (
                  <button
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    style={{ padding: "6px 14px", fontSize: 9, marginTop: 0 }}
                    onClick={() => onOpenAddVisit(order)}
                  >
                    + Log first visit
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.subTableSizer}>
                <div className={styles.subTableScroll}>
                  <table className={styles.subTable}>
                    <thead>
                      <tr className={styles.subTableHeadRow}>
                        <th className={styles.subTh}>Milestone</th>
                        <th className={styles.subTh}>Date</th>
                        <th className={styles.subTh}>Description</th>
                        <th className={styles.subTh}>Concern</th>
                        <th className={styles.subTh}>Cleared</th>
                        <th className={styles.subTh}>Flag</th>
                        <th className={styles.subTh}>Paid (₹)</th>
                        <th className={styles.subTh}>Documents</th>
                        {canWrite && <th className={styles.subTh}>Edit</th>}
                        {canWrite && <th className={styles.subTh}>Delete</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {visits.map((v) => (
                        <tr key={v.id} className={styles.subTr}>
                          {/* Milestone */}
                          <td className={styles.subTd}>
                            <span className={styles.milestoneBadge}>
                              {v.milestone}
                            </span>
                          </td>

                          {/* Date */}
                          <td className={styles.subTd}>
                            {v.date ? formatDate(v.date) : "—"}
                          </td>

                          {/* Description */}
                          <td className={`${styles.subTd} ${styles.subTdWrap}`}>
                            {v.description || (
                              <span style={{ opacity: 0.35 }}>—</span>
                            )}
                          </td>

                          {/* Concern */}
                          <td className={`${styles.subTd} ${styles.subTdWrap}`}>
                            {v.concern?.trim() ? (
                              <span
                                style={{
                                  display: "inline-block",
                                  background: "rgba(220,100,100,0.10)",
                                  border: "1px solid rgba(220,100,100,0.22)",
                                  borderRadius: 5,
                                  padding: "3px 8px",
                                  fontSize: 10,
                                  color: "#e08080",
                                  whiteSpace: "normal",
                                  lineHeight: 1.5,
                                }}
                              >
                                {v.concern}
                              </span>
                            ) : (
                              <span style={{ opacity: 0.35 }}>—</span>
                            )}
                          </td>

                          {/* Cleared toggle */}
                          <td className={styles.subTd}>
                            <Toggle
                              checked={!!v.cleared}
                              disabled={!canWrite}
                              onChange={() => onToggleCleared(orderId, v)}
                            />
                          </td>

                          {/* Flag toggle — visible to all, togglable by admin only */}
                          <td className={styles.subTd}>
                            <Toggle
                              checked={!!v.flagged}
                              disabled={!isAdmin}
                              trackStyle={
                                v.flagged
                                  ? { borderColor: "rgba(220,160,60,0.6)" }
                                  : {}
                              }
                              onChange={() => onToggleFlagged(orderId, v)}
                            />
                          </td>

                          {/* Paid */}
                          <td className={styles.subTd}>
                            {v.paidINR
                              ? `₹${Number(v.paidINR).toLocaleString("en-IN")}`
                              : "—"}
                          </td>

                          {/* Documents */}
                          <td className={styles.subTd}>
                            {(v.documents || []).length > 0 ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 3,
                                }}
                              >
                                {v.documents.map((d, i) =>
                                  d.startsWith("http") ? (
                                    <a
                                      key={i}
                                      href={d}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{
                                        fontSize: 10,
                                        color: "var(--gold)",
                                        textDecoration: "none",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      ↗ {d.split("/").pop()}
                                    </a>
                                  ) : (
                                    <span
                                      key={i}
                                      style={{
                                        fontSize: 10,
                                        color: "var(--text-muted)",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      📄 {d}
                                    </span>
                                  ),
                                )}
                              </div>
                            ) : (
                              <span style={{ opacity: 0.35 }}>—</span>
                            )}
                          </td>

                          {/* Edit */}
                          {canWrite && (
                            <td className={styles.subTd}>
                              <button
                                className={styles.editBtn}
                                onClick={() => onOpenEditVisit(orderId, v)}
                                title="Edit visit"
                              >
                                ✎
                              </button>
                            </td>
                          )}

                          {/* Delete */}
                          {canWrite && (
                            <td className={styles.subTd}>
                              {v.deleteRequested && !isAdmin ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 9,
                                      opacity: 0.55,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Pending review
                                  </span>
                                  <button
                                    className={styles.btn}
                                    style={{ padding: "4px 10px", fontSize: 9 }}
                                    onClick={() =>
                                      onCancelDeleteRequest(orderId, v)
                                    }
                                    title="Cancel delete request"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : deleteConfirm === `visit:${v.id}` ? (
                                <div style={{ display: "flex", gap: 4 }}>
                                  <button
                                    className={`${styles.btn} ${styles.btnPrimary}`}
                                    style={{
                                      padding: "4px 10px",
                                      fontSize: 9,
                                      marginTop: 0,
                                      background: "#c84040",
                                      borderColor: "#c84040",
                                    }}
                                    onClick={() => onDeleteVisit(orderId, v)}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    className={styles.btn}
                                    style={{ padding: "4px 10px", fontSize: 9 }}
                                    onClick={onCancelDelete}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className={styles.editBtn}
                                  style={{ color: "#e08080" }}
                                  onClick={() => onDeleteVisit(orderId, v)}
                                  title={
                                    isAdmin
                                      ? "Delete visit"
                                      : "Request deletion of this visit"
                                  }
                                >
                                  ✕
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
});

export default VisitSubTable;
